import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Backtest.css';
import './Backtest2.css';
import './MemberView.css';
import './NewMemberView.css';
import './App.css';  // company-popup modal styles, shared with the screener and logbook
import './Top10.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const usd = (n, dp = 2) =>
  n === null || n === undefined || Number.isNaN(n)
    ? '--'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: dp, maximumFractionDigits: dp });

const prettyDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US',
    { month: 'long', day: 'numeric', year: 'numeric' });
};

const parseAmount = (raw) => {
  const cleaned = String(raw).replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
};

const formatInput = (raw) => {
  const cleaned = String(raw).replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const [int, ...rest] = cleaned.split('.');
  const grouped = int ? parseInt(int, 10).toLocaleString('en-US') : '';
  if (rest.length === 0) return grouped;
  return `${grouped}.${rest.join('').slice(0, 2)}`;
};

const Top10 = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState('');
  const [mode, setMode] = useState('fractional');
  const [openDisclosure, setOpenDisclosure] = useState(null);
  const [signal, setSignal] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/top10/signal`, { timeout: 60000 })
      .then((r) => { if (alive) setSignal(r.data); })
      .catch(() => { if (alive) setSignal({ status: 'error' }); });
    return () => { alive = false; };
  }, []);
  const [popup, setPopup] = useState({ show: false, data: null, loading: false });

  const openCompany = async (symbol, name) => {
    setPopup({ show: true, loading: true, data: { symbol, company_name: name } });
    try {
      const res = await axios.get(`${API_URL}/api/company/${symbol}`, { timeout: 30000 });
      const d = res.data;
      setPopup({
        show: true,
        loading: false,
        data: d.status === 'success' && d.company_description_full
          ? d
          : { symbol, company_name: name, company_description_full: 'A description for this company is not available right now.' },
      });
    } catch (e) {
      setPopup({
        show: true,
        loading: false,
        data: { symbol, company_name: name, company_description_full: 'The description could not be loaded. Please try again in a moment.' },
      });
    }
  };

  const closeCompany = () => setPopup({ show: false, data: null, loading: false });

  // Built and downloaded entirely in the browser. The amount never touches the server,
  // which is the whole point: the member keeps the only copy.
  const downloadCSV = () => {
    const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const today = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Pro Stock Money Bowl, QQQ Top 10 Calculator'],
      ['Educational illustration only. Not investment advice and not a recommendation.'],
      [],
      ['Generated', today],
      ['Amount entered', amount.toFixed(2)],
      ['Share mode', mode === 'fractional' ? 'Fractional shares' : 'Whole shares only'],
      ['Prices as of close', data.price_date || ''],
      ['Exit signal when downloaded', signal && signal.status === 'success'
        ? `${signal.in_market ? 'In the market' : 'Stepped aside'} as of ${signal.as_of_date}`
        : 'Unknown, we could not read it'],
      ['Next quarterly reset', reb.next_label],
      [],
      ['Symbol', 'Company', 'Target weight %', 'Price', 'Shares', 'Dollars'],
      ...lines.map((l) => [
        l.symbol,
        l.name,
        l.weight_pct.toFixed(2),
        l.price === null ? '' : l.price.toFixed(2),
        l.shares === null ? '' : (mode === 'fractional' ? l.shares.toFixed(4) : String(l.shares)),
        l.deployed.toFixed(2),
      ]),
      [],
      ['Total invested', '', '100.00', '', '', deployed.toFixed(2)],
      ['Cash remainder', '', '', '', '', leftover.toFixed(2)],
      [],
      ['The ten largest Nasdaq-100 companies, weighted by size, no single name above 25%, reset quarterly.'],
      ['Prices move every day, so these share counts are only accurate for the day shown above.'],
      ['The exit signal can move on any day. This file does not update. Check the page.'],
      ['Nothing on this sheet was saved by Pro Stock Money Bowl. This file is your only copy.'],
      ['Speak with a licensed advisor and your tax professional before acting on any of it.'],
    ];
    const csv = rows.map((r) => r.map(q).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `top10-calculator-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!popup.show) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest('.company-popup') && !e.target.closest('.t10-company-btn')) closeCompany();
    };
    const onEsc = (e) => { if (e.key === 'Escape') closeCompany(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [popup.show]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/top10/basket`, { timeout: 120000 });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError('The basket could not be loaded right now. Please refresh in a moment.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const amount = parseAmount(raw);

  const lines = useMemo(() => {
    if (!data) return [];
    const wSum = data.holdings.reduce((s, h) => s + h.weight, 0) || 1;
    return data.holdings.map((h) => {
      const dollars = amount * (h.weight / wSum);
      const sharesExact = h.price ? dollars / h.price : null;
      const shares = sharesExact === null
        ? null
        : mode === 'fractional'
          ? Math.round(sharesExact * 10000) / 10000
          : Math.floor(sharesExact);
      // Fractional buys land exactly on the dollar target. Whole shares round down and
      // leave a remainder, which is the whole point of showing the two modes.
      const deployed = mode === 'fractional' ? dollars : (shares === null ? 0 : shares * h.price);
      return { ...h, dollars, shares, deployed };
    });
  }, [data, amount, mode]);

  const deployed = lines.reduce((s, l) => s + l.deployed, 0);
  const leftover = amount > 0 ? Math.max(0, Math.round((amount - deployed) * 100) / 100) : 0;
  const hasAmount = amount > 0;

  if (loading) {
    return (
      <div className="bt-container mv-page" data-testid="top10-loading">
        <div className="t10-loading">Loading the current basket…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bt-container mv-page" data-testid="top10-error">
        <div className="t10-error">{error || 'No data.'}</div>
      </div>
    );
  }

  const reb = data.rebalance;
  const perf = data.performance;

  return (
    <div className="bt-container mv-page t10-page" data-testid="top10-page">
      <div className="bt-header mv-header-stack">
        <div className="t10-eyebrow">GRATEFUL TRUST · THE EXPLORE SIDE OF YOUR PORTFOLIO</div>
        <h1 className="bt2-title mv-title t10-title">QQQ Top 10 Calculator</h1>
        <div className="mv-subtitle">
          The ten largest Nasdaq-100 companies, weighted by size, capped at 25% each, reset every quarter.
        </div>
        <p className="mv-description">
          This is a teaching tool, not a recommendation. It shows you what the model basket looks
          like today and does the arithmetic on whatever number you type in. Nothing you enter is
          sent anywhere, nothing is saved, and there is no account. Come back any time, enter your
          current balance, and compare it against what you are actually holding.
        </p>
      </div>

      {data.stale && (
        <div className="t10-stale" data-testid="top10-stale-banner">
          Live prices are temporarily unavailable, so this is the last basket we were able to
          load. Treat the share counts as approximate until it refreshes.
        </div>
      )}

      {/* Reduced signal only. The full signal lives behind the member passcode on /signal. */}
      <section
        className={`t10-signal ${signal && signal.status === 'success'
          ? (signal.in_market ? 'is-on' : 'is-off') : 'is-unknown'}`}
        data-testid="top10-signal-strip"
      >
        <div className="t10-signal-dot" aria-hidden="true" />
        <div className="t10-signal-body">
          {!signal && <strong>Checking the exit signal…</strong>}
          {signal && signal.status !== 'success' && (
            <>
              <strong data-testid="top10-signal-state">Signal unavailable right now</strong>
              <span>
                We could not read today's signal. Do not assume it has not moved. Check the
                signal page before you act on anything here.
              </span>
            </>
          )}
          {signal && signal.status === 'success' && signal.in_market && (
            <>
              <strong data-testid="top10-signal-state">
                The system is in the market as of {prettyDate(signal.as_of_date)}
              </strong>
              <span>
                Holding. The exit signal for this basket comes from the index, not from these
                ten names, and it has not moved.
              </span>
            </>
          )}
          {signal && signal.status === 'success' && !signal.in_market && (
            <>
              <strong data-testid="top10-signal-state">
                The system has stepped aside as of {prettyDate(signal.as_of_date)}
              </strong>
              <span>
                The exit signal has been given. If you are running this basket, this is the
                day it matters.
              </span>
            </>
          )}
        </div>
      </section>

      {/* ---------- What this is ---------- */}
      <section className="t10-card" data-testid="top10-what-it-is">
        <h2 className="t10-h2">What this basket is</h2>
        <ul className="t10-rules">
          <li><strong>The ten largest.</strong> The ten biggest companies in the Nasdaq-100 as of today. Not a hand-picked list, not a named group like the Magnificent Seven. Whoever is largest is in.</li>
          <li><strong>Weighted by size.</strong> Bigger company, bigger share of the basket. The same idea the index itself uses.</li>
          <li><strong>Capped at 25%.</strong> No single company is allowed past a quarter of the basket. The overflow is pushed down into the other nine.</li>
          <li><strong>Reset quarterly.</strong> Four times a year the list is rebuilt and the weights are reset. Nothing happens in between.</li>
          <li><strong>The exit signal still comes from QQQ.</strong> Whether the system is in the market or stepped aside is decided by the index, never by these ten names.</li>
        </ul>
        <p className="t10-note">{data.source_note}</p>
      </section>

      {/* ---------- Calculator ---------- */}
      <section className="t10-calc" data-testid="top10-calculator">
        <h2 className="t10-h2">Run your own number</h2>
        <div className="t10-input-row">
          <label className="t10-label" htmlFor="t10-amount">Amount you want to illustrate</label>
          <div className="t10-input-wrap">
            <span className="t10-dollar">$</span>
            <input
              id="t10-amount"
              className="t10-input"
              inputMode="decimal"
              maxLength={15}
              placeholder="25,000"
              value={raw}
              onChange={(e) => setRaw(formatInput(e.target.value))}
              data-testid="top10-amount-input"
            />
          </div>
          {hasAmount && (
            <button
              type="button"
              className="t10-clear"
              onClick={() => setRaw('')}
              data-testid="top10-clear-button"
            >
              Clear
            </button>
          )}
        </div>

        <div className="t10-mode" role="group" aria-label="Share mode">
          <button
            type="button"
            className={`t10-mode-btn ${mode === 'fractional' ? 'is-on' : ''}`}
            onClick={() => setMode('fractional')}
            data-testid="top10-mode-fractional"
          >
            Fractional shares
          </button>
          <button
            type="button"
            className={`t10-mode-btn ${mode === 'whole' ? 'is-on' : ''}`}
            onClick={() => setMode('whole')}
            data-testid="top10-mode-whole"
          >
            Whole shares only
          </button>
        </div>
        <p className="t10-mode-hint">
          {mode === 'fractional'
            ? 'Fractional means you buy by dollar amount and your broker splits the share for you. Every weight lands exactly on target.'
            : 'Whole shares only rounds each position down to a full share, which leaves a cash remainder. This is what a broker without fractional trading looks like.'}
        </p>

        <div className="t10-table" data-testid="top10-basket-table">
          <div className="t10-row t10-row--head">
            <span className="t10-c-sym">Company</span>
            <span className="t10-c-num">Weight</span>
            <span className="t10-c-num">Dollars</span>
            <span className="t10-c-num">Price</span>
            <span className="t10-c-num">Shares</span>
          </div>
          {lines.map((l) => (
            <div className="t10-row" key={l.symbol} data-testid={`top10-row-${l.symbol}`}>
              <span className="t10-c-sym">
                <button
                  type="button"
                  className="t10-company-btn"
                  onClick={() => openCompany(l.symbol, l.name)}
                  title={`What does ${l.symbol} do?`}
                  data-testid={`top10-company-${l.symbol}`}
                >
                  <strong>{l.symbol}</strong>
                  <span className="t10-coname">{l.name}</span>
                </button>
                {l.capped && <span className="t10-capped-tag">capped at 25%</span>}
              </span>
              <span className="t10-c-num" data-label="Weight">
                <span className="t10-weight">{l.weight_pct.toFixed(2)}%</span>
              </span>
              <span className="t10-c-num" data-label="Dollars">
                {hasAmount ? usd(l.deployed, 2) : <span className="t10-dash">--</span>}
              </span>
              <span className="t10-c-num" data-label="Price">
                {l.price ? usd(l.price, 2) : <span className="t10-dash">--</span>}
              </span>
              <span className="t10-c-num" data-label="Shares">
                {hasAmount && l.shares !== null
                  ? (mode === 'fractional' ? l.shares.toFixed(4) : l.shares.toLocaleString('en-US'))
                  : <span className="t10-dash">--</span>}
              </span>
            </div>
          ))}
          <div className="t10-row t10-row--total" data-testid="top10-totals">
            <span className="t10-c-sym"><strong>Total</strong></span>
            <span className="t10-c-num" data-label="Weight">100.00%</span>
            <span className="t10-c-num" data-label="Invested">{hasAmount ? usd(deployed, 2) : <span className="t10-dash">--</span>}</span>
            <span className="t10-c-num" />
            <span className="t10-c-num" data-label="Cash left">
              {hasAmount ? <span className="t10-left">{usd(leftover, 2)} cash</span> : <span className="t10-dash">--</span>}
            </span>
          </div>
        </div>

        <div className="t10-meta">
          Prices are the closing prices from{' '}
          <strong>{prettyDate(data.price_date) || 'the last session'}</strong>.
          They move every day, so the share counts here are only accurate for the moment you ran them.
          Tap any company name to read what that business actually does.
        </div>

        <div className="t10-watch" data-testid="top10-watch-warning">
          <div className="t10-watch-head">This page does not watch itself</div>
          <p>
            The rebalance date is on the calendar, but the exit is not. It can arrive any day,
            and nothing here will come and find you. The strip at the top of this page shows
            whether the system is in the market or has stepped aside, and it is only true for
            the moment you loaded the page. If you are going to run this basket, you are
            agreeing to look. If you are not going to look, this belongs in Core instead,
            where one holding and a set of rules do the watching for you.
          </p>
        </div>

        <div className="t10-export">
          <button
            type="button"
            className="t10-export-btn"
            onClick={downloadCSV}
            disabled={!hasAmount}
            data-testid="top10-download-csv"
          >
            Download this as a CSV
          </button>
          <span className="t10-export-hint">
            {hasAmount
              ? 'Opens in Excel, Numbers or Google Sheets, so you can have it beside you when you place orders. We keep no copy, this file is yours.'
              : 'Enter an amount above and you can take the whole table with you as a spreadsheet.'}
          </span>
        </div>
      </section>

      {/* ---------- Rebalance date ---------- */}
      <section className="t10-reb" data-testid="top10-rebalance">
        <div className="t10-reb-left">
          <div className="t10-reb-eyebrow">Next reset date</div>
          <div className="t10-reb-date" data-testid="top10-next-rebalance-date">{reb.next_label}</div>
          <div className="t10-reb-days">
            {reb.is_today ? 'That is today.' : `${reb.days_away} day${reb.days_away === 1 ? '' : 's'} away`}
          </div>
        </div>
        <div className="t10-reb-right">
          <p>{reb.rule}</p>
          <p className="t10-note">
            The last reset was <strong>{reb.last_label}</strong>. Between reset dates the basket is
            left alone, even when the weights drift. That patience is part of why quarterly tested
            better than monthly.
          </p>
        </div>
      </section>

      {/* ---------- Dynamic readback ---------- */}
      <section className="t10-readback" data-testid="top10-readback">
        <h2 className="t10-h2">What you just did</h2>
        {hasAmount ? (
          <>
            <p>
              You entered <strong>{usd(amount, 0)}</strong> as an illustration. Someone choosing to
              run this basket themselves, in their own account, with their own money, would be
              holding those ten positions in those proportions, with roughly{' '}
              <strong>{usd(lines[0].deployed, 0)}</strong> of it in {lines[0].symbol} as the largest
              single line.
            </p>
            <p>
              {mode === 'fractional'
                ? 'Because you chose fractional shares, each position is bought as a dollar amount and the weights land exactly where they should.'
                : `Because you chose whole shares only, each position rounds down to a full share and about ${usd(leftover, 0)} is left sitting in cash. That remainder is normal, it is not an error.`}
            </p>
            <p>
              On <strong>{reb.next_label}</strong> the work is to come back here, enter the value
              of the part of your portfolio you are running this way as it stands on that day, and
              compare the new weights against what is actually in the account. Positions that have grown past their new weight get trimmed,
              positions that have fallen behind get added to, and any company that has dropped out
              of the ten gets replaced. Then nothing happens again until the quarter after that.
            </p>
            <p className="t10-readback-foot">
              We are not telling you to do this, and we are not placing any of it for you. You
              entered a number into a calculator and it showed you the arithmetic. Whether any of
              it belongs in your account is your decision, and if you want it checked against your
              own situation, that is a conversation for a licensed advisor and your tax
              professional.
            </p>
          </>
        ) : (
          <p>
            Enter a dollar amount above and this section will walk back through exactly what the
            numbers mean, what a person doing this themselves would be holding, and what the work
            looks like on the next reset date.
          </p>
        )}
      </section>

      {/* ---------- Performance ---------- */}
      <section className="t10-card" data-testid="top10-performance">
        <h2 className="t10-h2">What the research found</h2>
        <div className="t10-perf-window">{perf.window}, starting from {usd(perf.start_value, 0)}</div>
        <div className="t10-perf" data-testid="top10-performance-table">
          {perf.rows.map((r) => (
            <div className={`t10-perf-row ${r.highlight ? 'is-strategy' : ''}`} key={r.name}>
              <div className="t10-perf-name">{r.name}</div>
              <div className="t10-perf-grid">
                <div><span>Growth rate a year</span><strong>{r.cagr.toFixed(2)}%</strong></div>
                <div><span>Worst drop</span><strong className="t10-red">{r.max_dd.toFixed(2)}%</strong></div>
                <div><span>Return per unit of risk</span><strong>{r.sharpe.toFixed(2)}</strong></div>
                <div><span>Ended at</span><strong>{usd(r.ending, 0)}</strong></div>
              </div>
            </div>
          ))}
        </div>
        <div className="t10-flat" data-testid="top10-flat-patch">
          <div className="t10-flat-head">{perf.flat_patch.headline}</div>
          <p>{perf.flat_patch.detail}</p>
        </div>
        <p className="t10-note">
          Over the full record we can measure, {perf.full_record.window}, the basket grew{' '}
          {perf.full_record.cagr.toFixed(2)}% a year with a worst drop of{' '}
          {perf.full_record.max_dd.toFixed(2)}% and about {perf.full_record.turnover_pct_yr}% of the
          basket changing hands a year. These are results from a reconstruction we built ourselves.
          They are not an audited index and they are not a forecast.
        </p>
      </section>

      {/* ---------- Disclosures ---------- */}
      <section className="t10-card" data-testid="top10-disclosures">
        <h2 className="t10-h2">Six things to call to your attention</h2>
        <div className="t10-disc-list">
          {data.disclosures.map((d, i) => (
            <div className={`t10-disc ${openDisclosure === i ? 'is-open' : ''}`} key={d.title}>
              <button
                type="button"
                className="t10-disc-head"
                onClick={() => setOpenDisclosure(openDisclosure === i ? null : i)}
                data-testid={`top10-disclosure-${i}`}
              >
                <span>{d.title}</span>
                <span className="t10-disc-chev">{openDisclosure === i ? '–' : '+'}</span>
              </button>
              <div className="t10-disc-body">
                <p>{d.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="t10-legal" data-testid="top10-legal">
        <strong>Educational use only.</strong> Pro Stock Money Bowl and the Grateful Trust community
        are not registered investment advisors, and nothing on this page is personalized investment
        advice, a recommendation, or an offer to buy or sell any security. The figures shown are
        illustrations produced by a calculator from a number you typed in. Past results, including
        results from a historical reconstruction like this one, do not predict future results. You
        could lose money. Before acting on anything here, speak with a licensed advisor and a tax
        professional who know your full situation. Nothing you enter on this page is transmitted,
        logged or stored.
      </div>

      {popup.show && popup.data && (
        <div className="company-popup" data-testid="top10-company-popup">
          <div className="company-popup-content">
            <div className="company-popup-header">
              <span className="company-popup-symbol">{popup.data.symbol}</span>
              <span className="company-popup-title">{popup.data.company_name}</span>
              <button
                className="company-popup-close"
                onClick={closeCompany}
                data-testid="top10-company-popup-close"
              >
                ×
              </button>
            </div>
            <div className="company-popup-details">
              {popup.data.business_description && (
                <div className="company-popup-industry">
                  <span className="industry-label">{popup.data.business_description}</span>
                </div>
              )}
              <div className="company-popup-description">
                {popup.loading
                  ? <p><em>Loading description…</em></p>
                  : <p>{popup.data.company_description_full}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Top10;
